import * as React from "react";
import { supabase } from "@/integrations/supabase/client";

export type Tx = {
    id: string;
    bank_account_id: string;
    transaction_date: string; // date or timestamp
    end_balance: number | null;
    end_balance_currency: string | null;
};

export type UserProfileLite = {
    id: string;
    first_name: string | null;
    last_name: string | null;
};

export type BalanceSnapshot = {
    id: string;
    bank_account_id: string;
    balance_date: string; // date
    closing_balance: number | null;
    currency: string;
    created_at: string | null; // timestamp
    user_id: string | null;
};

export type ComputedMeta = {
    _computed_current_balance: number | null;
    _computed_currency: string;
    _computed_source: "snapshot" | "transactions";
    _computed_balance_date: string | null;
    _computed_entered_at: string | null;
    _computed_entered_by: string | null;
};

const toLocalISODate = (d?: string | null) => {
    if (!d) return null;
    const dt = new Date(d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

/**
 * Decide which source is "newer":
 * - Compare snapshot.balance_date vs tx.transaction_date
 * - If equal date, prefer manual snapshot (manual override for that date)
 */
const chooseBestSource = (snap: BalanceSnapshot | null, tx: Tx | null): {
    source: "snapshot" | "transactions" | null;
    snap: BalanceSnapshot | null;
    tx: Tx | null;
} => {
    if (!snap && !tx) return { source: null, snap: null, tx: null };
    if (snap && !tx) return { source: "snapshot", snap, tx: null };
    if (!snap && tx) return { source: "transactions", snap: null, tx };

    const sDate = snap!.balance_date;                // date: YYYY-MM-DD
    const tDate = toLocalISODate(tx!.transaction_date); // timestamp -> YYYY-MM-DD (локально)

    if (tDate && sDate !== tDate) {
        return Date.parse(sDate) > Date.parse(tDate)
            ? { source: "snapshot", snap: snap!, tx: null }
            : { source: "transactions", snap: null, tx: tx! };
    }

    // same calendar day -> manual snapshot wins
    return { source: "snapshot", snap: snap!, tx: null };
};

const formatUserName = (
    p?: { first_name?: string | null; last_name?: string | null } | null,
    fallbackId?: string | null
) => {
    const full = [p?.first_name, p?.last_name].filter(Boolean).join(" ");
    return (
        full || (fallbackId ? `User ${String(fallbackId).slice(0, 8)}…` : "Unknown user")
    );
};

export const pickLatestSnapshot = (list: BalanceSnapshot[]) => {
    if (!list.length) return null;

    return [...list].sort((a, b) => {
        const ad = Date.parse(a.balance_date);
        const bd = Date.parse(b.balance_date);
        if (ad !== bd) return bd - ad;

        const ac = a.created_at ? Date.parse(a.created_at) : 0;
        const bc = b.created_at ? Date.parse(b.created_at) : 0;
        if (ac !== bc) return bc - ac;

        return String(b.id).localeCompare(String(a.id));
    })[0];
};

export const pickLatestTx = (list: Tx[]) => {
    if (!list.length) return null;

    const withBalance = list.filter((t) => t.end_balance !== null);
    const base = withBalance.length ? withBalance : list;

    return [...base].sort((a, b) => {
        const ad = Date.parse(a.transaction_date);
        const bd = Date.parse(b.transaction_date);
        if (ad !== bd) return bd - ad;
        return String(b.id).localeCompare(String(a.id));
    })[0];
};

export const buildSnapshotsByAccount = (snapshots: BalanceSnapshot[]) => {
    const map = new Map<string, BalanceSnapshot[]>();
    for (const s of snapshots) {
        const arr = map.get(s.bank_account_id) ?? [];
        arr.push(s);
        map.set(s.bank_account_id, arr);
    }
    return map;
};

export const buildTxByAccount = (txs: Tx[]) => {
    const map = new Map<string, Tx[]>();
    for (const t of txs) {
        const arr = map.get(t.bank_account_id) ?? [];
        arr.push(t);
        map.set(t.bank_account_id, arr);
    }
    return map;
};

type UseComputedBankBalancesArgs<
    TAccount extends { id: string; currency?: string | null; name?: string | null }
> = {
    accounts: TAccount[];
    isAccountsLoading: boolean;
    fallbackCurrency?: string;
    limit?: number;
    refreshKey?: number;
};

export function useComputedBankBalances<
    TAccount extends { id: string; currency?: string | null; name?: string | null }
>({
    accounts,
    isAccountsLoading,
    fallbackCurrency = "ETB",
    limit = 5000,
    refreshKey = 0,
}: UseComputedBankBalancesArgs<TAccount>) {
    const [txLoading, setTxLoading] = React.useState(false);
    const [txs, setTxs] = React.useState<Tx[]>([]);

    const [countsLoading, setCountsLoading] = React.useState(false);
    const [transactionsCountByAccount, setTransactionsCountByAccount] = React.useState<
        Map<string, number>
    >(new Map());

    const [balLoading, setBalLoading] = React.useState(false);
    const [snapshots, setSnapshots] = React.useState<BalanceSnapshot[]>([]);
    const [profilesById, setProfilesById] = React.useState<Map<string, UserProfileLite>>(
        new Map()
    );

    // Load snapshots + profiles
    React.useEffect(() => {
        if (!accounts.length) {
            setSnapshots([]);
            setProfilesById(new Map());
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setBalLoading(true);
                const accountIds = accounts.map((a) => a.id);

                const { data, error } = await supabase
                    .from("bank_account_balances")
                    .select(
                        `
            id,
            bank_account_id,
            balance_date,
            closing_balance,
            currency,
            created_at,
            user_id
          `
                    )
                    .in("bank_account_id", accountIds)
                    .order("balance_date", { ascending: false })
                    .order("created_at", { ascending: false })
                    .limit(limit);

                if (error) throw error;

                const rows = (data ?? []) as BalanceSnapshot[];
                if (!cancelled) setSnapshots(rows);

                const userIds = Array.from(
                    new Set(rows.map((r) => r.user_id).filter((x): x is string => !!x))
                );

                if (!userIds.length) {
                    if (!cancelled) setProfilesById(new Map());
                    return;
                }

                const { data: профили, error: профErr } = await supabase
                    .from("user_profiles")
                    .select("id, first_name, last_name")
                    .in("id", userIds);

                if (профErr) throw профErr;

                const map = new Map<string, UserProfileLite>();
                for (const p of (профили ?? []) as UserProfileLite[]) map.set(p.id, p);
                if (!cancelled) setProfilesById(map);
            } catch (e) {
                console.error("Failed to load bank_account_balances / profiles:", e);
                if (!cancelled) {
                    setSnapshots([]);
                    setProfilesById(new Map());
                }
            } finally {
                if (!cancelled) setBalLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [accounts, limit, refreshKey]);

    // Load transactions (latest rows, limited)
    React.useEffect(() => {
        if (!accounts.length) {
            setTxs([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setTxLoading(true);
                const accountIds = accounts.map((a) => a.id);

                const { data, error } = await supabase
                    .from("bank_transactions")
                    .select("id, bank_account_id, transaction_date, end_balance, end_balance_currency")
                    .in("bank_account_id", accountIds)
                    .order("transaction_date", { ascending: false })
                    .limit(limit);

                if (error) throw error;
                if (!cancelled) setTxs((data ?? []) as Tx[]);
            } catch (e) {
                console.error("Failed to load transactions for balances:", e);
                if (!cancelled) setTxs([]);
            } finally {
                if (!cancelled) setTxLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [accounts, limit]);

    // Load exact transactions counts (from view)
    React.useEffect(() => {
        if (!accounts.length) {
            setTransactionsCountByAccount(new Map());
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setCountsLoading(true);
                const accountIds = accounts.map((a) => a.id);

                const { data, error } = await supabase
                    .from("bank_transactions_counts")
                    .select("bank_account_id, transactions_count")
                    .in("bank_account_id", accountIds);

                if (error) throw error;

                const map = new Map<string, number>();
                for (const row of data ?? []) {
                    map.set(row.bank_account_id, Number(row.transactions_count ?? 0));
                }

                if (!cancelled) setTransactionsCountByAccount(map);
            } catch (e) {
                console.error("Failed to load transactions counts:", e);
                if (!cancelled) setTransactionsCountByAccount(new Map());
            } finally {
                if (!cancelled) setCountsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [accounts]);

    const txByAccount = React.useMemo(() => buildTxByAccount(txs), [txs]);
    const snapshotsByAccount = React.useMemo(() => buildSnapshotsByAccount(snapshots), [snapshots]);

    const computedAccounts = React.useMemo(() => {
        return accounts.map((account) => {
            const snapList = snapshotsByAccount.get(account.id) ?? [];
            const latestSnap = pickLatestSnapshot(snapList);

            const txList = txByAccount.get(account.id) ?? [];
            const latestTx = pickLatestTx(txList);

            const best = chooseBestSource(latestSnap, latestTx);

            if (best.source === "snapshot") {
                const s = best.snap!;
                const prof = s.user_id ? profilesById.get(s.user_id) : undefined;

                return {
                    ...account,
                    _computed_current_balance: s.closing_balance ?? null,
                    _computed_currency: s.currency ?? account.currency ?? fallbackCurrency,
                    _computed_source: "snapshot" as const,
                    _computed_balance_date: s.balance_date,
                    _computed_entered_at: s.created_at,
                    _computed_entered_by: formatUserName(
                        prof ? { first_name: prof.first_name, last_name: prof.last_name } : null,
                        s.user_id
                    ),
                } as TAccount & ComputedMeta;
            }

            if (best.source === "transactions") {
                const t = best.tx!;
                return {
                    ...account,
                    _computed_current_balance: t.end_balance ?? null,
                    _computed_currency: t.end_balance_currency ?? account.currency ?? fallbackCurrency,
                    _computed_source: "transactions" as const,
                    _computed_balance_date: t.transaction_date ?? null,
                    _computed_entered_at: null,
                    _computed_entered_by: null,
                } as TAccount & ComputedMeta;
            }

            // neither exists
            return {
                ...account,
                _computed_current_balance: null,
                _computed_currency: account.currency ?? fallbackCurrency,
                _computed_source: "transactions" as const, // keeping existing union; UI can treat null balance as "no data"
                _computed_balance_date: null,
                _computed_entered_at: null,
                _computed_entered_by: null,
            } as TAccount & ComputedMeta;
        });
    }, [accounts, snapshotsByAccount, txByAccount, profilesById, fallbackCurrency]);

    const balancesByCurrency = React.useMemo(() => {
        return computedAccounts.reduce((acc, account) => {
            const currency = account._computed_currency || fallbackCurrency;
            const bal = account._computed_current_balance ?? 0;
            acc[currency] = (acc[currency] ?? 0) + bal;
            return acc;
        }, {} as Record<string, number>);
    }, [computedAccounts, fallbackCurrency]);

    const showLoading = isAccountsLoading || txLoading || balLoading || countsLoading;

    return {
        // raw
        txs,
        snapshots,
        profilesById,

        // grouped
        txByAccount,
        snapshotsByAccount,

        // computed
        computedAccounts,
        balancesByCurrency,

        // counts
        transactionsCountByAccount,

        // loading
        txLoading,
        balLoading,
        countsLoading,
        showLoading,
    };
}